import type { OrgBranding, OrgTheme, ThemeToken } from '../types';

/**
 * The tenant-controlled palette, and the arithmetic that keeps it readable.
 *
 * This lives under /shared rather than /lib because the Functions need it too:
 * the page a client opens is served by a Cloud Function, and a white-label
 * product whose one client-facing page ignores the tenant's colours is not
 * white-label. One definition, both sides, per CLAUDE.md §4.
 *
 * Every colour in the product already resolves through a CSS custom property,
 * so branding is a runtime swap and never a rebuild. What this module adds is
 * which of those properties a tenant may set, what they fall back to, and how
 * the rest are derived so a badly chosen pair can never produce unreadable text.
 *
 * Status colours are deliberately absent from what a tenant may set. Amber
 * means a timer is running, red means alert, and they have to mean that in
 * every organisation — a support conversation stops being portable the moment
 * one tenant's "blocked" is another's "done". Status is carried by shape and
 * label as well as hue, so nothing here depends on colour alone.
 */

export interface ThemeTokenSpec {
  key: ThemeToken;
  cssVar: string;
  label: string;
  hint: string;
}

export const THEME_TOKENS: readonly ThemeTokenSpec[] = [
  {
    key: 'accent',
    cssVar: '--accent',
    label: 'Accent',
    hint: 'Highlights, and the colour of outgoing email.',
  },
  {
    key: 'ink',
    cssVar: '--ink',
    label: 'Ink',
    hint: 'Body text, and the fill of every solid button. The text on top of it is chosen for contrast automatically.',
  },
  {
    key: 'paper',
    cssVar: '--paper',
    label: 'Surface',
    hint: 'Cards, dialogs and the sidebar.',
  },
  {
    key: 'canvas',
    cssVar: '--canvas',
    label: 'Background',
    hint: 'What the cards float on.',
  },
  {
    key: 'canvasTint',
    cssVar: '--canvas-tint',
    label: 'Subtle fill',
    hint: 'Table headers, hovered rows, quiet panels.',
  },
  { key: 'line', cssVar: '--line', label: 'Divider', hint: 'Row and section rules.' },
  {
    key: 'lineStrong',
    cssVar: '--line-strong',
    label: 'Border',
    hint: 'Input outlines and secondary buttons.',
  },
  {
    key: 'textMuted',
    cssVar: '--text-muted',
    label: 'Muted text',
    hint: 'Labels, metadata, secondary rows.',
  },
  {
    key: 'textFaint',
    cssVar: '--text-faint',
    label: 'Faint text',
    hint: 'Placeholders and disabled controls.',
  },
];

export type ResolvedTheme = Record<ThemeToken, string>;

/** The monochrome base. Matches the `:root` block in index.css exactly. */
export const DEFAULT_THEME: ResolvedTheme = {
  accent: '#ffffff',
  ink: '#ffffff',
  paper: '#101010',
  canvas: '#000000',
  canvasTint: '#161616',
  line: '#2a2a2a',
  lineStrong: '#484848',
  textMuted: '#a0a0a0',
  textFaint: '#6e6e6e',
};

// ---------- colour arithmetic ----------

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export function parseHex(value: string): Rgb | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(value.trim());
  if (!match) return null;
  const int = Number.parseInt(match[1]!, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

export function toHex({ r, g, b }: Rgb): string {
  const part = (n: number) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`;
}

/** WCAG relative luminance, used to decide what goes on top of a colour. */
export function luminance(color: Rgb): number {
  const channel = (raw: number) => {
    const c = raw / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(color.r) + 0.7152 * channel(color.g) + 0.0722 * channel(color.b);
}

export function contrast(a: Rgb, b: Rgb): number {
  const [high, low] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (high! + 0.05) / (low! + 0.05);
}

export function mix(from: Rgb, to: Rgb, amount: number): Rgb {
  return {
    r: from.r + (to.r - from.r) * amount,
    g: from.g + (to.g - from.g) * amount,
    b: from.b + (to.b - from.b) * amount,
  };
}

export function rgba(color: Rgb, alpha: number): string {
  return `rgba(${Math.round(color.r)}, ${Math.round(color.g)}, ${Math.round(color.b)}, ${alpha})`;
}

export const WHITE: Rgb = { r: 255, g: 255, b: 255 };
export const BLACK: Rgb = { r: 0, g: 0, b: 0 };
/** The shadow's colour. Warmer than pure black, which reads as a hole. */
export const SHADOW_INK: Rgb = { r: 16, g: 17, b: 18 };

/**
 * What to print on a filled surface.
 *
 * Whichever of white and black contrasts better wins, so a tenant who picks a
 * pale ink gets dark button labels rather than white-on-cream.
 *
 * Pure black rather than the design system's near-black on purpose: the two
 * extremes cross over at 4.58:1, so this clears 4.5:1 against every colour a
 * tenant can choose. Near-black bottoms out at 4.39:1, which is a mid-grey ink
 * away from failing.
 */
export function readableOn(background: Rgb): Rgb {
  return contrast(background, WHITE) >= contrast(background, BLACK) ? WHITE : BLACK;
}

/** The four fixed status hues, as authored for a white surface. */
export const STATUS_COLORS = {
  running: '#b4620f',
  /** The stop control. A true red, not the brown-red that alert carries. */
  stop: '#d92d20',
  alert: '#a8341f',
  urgent: '#8a2415',
  normal: '#2e6b41',
} as const;

export type StatusColor = keyof typeof STATUS_COLORS;

function toHsl({ r, g, b }: Rgb): { h: number; s: number; l: number } {
  const [rr, gg, bb] = [r / 255, g / 255, b / 255];
  const max = Math.max(rr, gg, bb);
  const min = Math.min(rr, gg, bb);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h =
    max === rr
      ? ((gg - bb) / d + (gg < bb ? 6 : 0)) * 60
      : max === gg
        ? ((bb - rr) / d + 2) * 60
        : ((rr - gg) / d + 4) * 60;
  return { h, s, l };
}

function fromHsl(h: number, s: number, l: number): Rgb {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    return 255 * (l - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1))));
  };
  return { r: f(0), g: f(8), b: f(4) };
}

/**
 * The same colour, made legible on a given surface.
 *
 * Hue and saturation are held; only lightness moves, and only far enough to
 * clear the contrast floor. That is what lets the status colours stay
 * tenant-independent in meaning while still being readable on a dark surface:
 * amber is still amber, it is simply a lighter amber when the paper is nearly
 * black.
 *
 * Without this, the shipped palette on the Midnight preset measures 1.97:1 for
 * urgent and 2.67:1 for alert against its own paper — a red that cannot be read
 * carries no meaning at all.
 */
export function adaptToSurface(color: string, surface: Rgb, minRatio = 4.5): string {
  const parsed = parseHex(color);
  if (!parsed) return color;
  if (contrast(parsed, surface) >= minRatio) return color;

  const { h, s } = toHsl(parsed);
  // Move away from the surface: lighten on a dark one, darken on a light one.
  const up = luminance(surface) < 0.5;
  const start = toHsl(parsed).l;

  let best = parsed;
  let bestRatio = contrast(parsed, surface);
  for (let step = 1; step <= 100; step += 1) {
    const l = up ? Math.min(1, start + step / 100) : Math.max(0, start - step / 100);
    // Measured after the round trip through hex, which is what gets stored:
    // a ratio that passed before rounding and fails after is a failure.
    const candidate = parseHex(toHex(fromHsl(h, s, l)))!;
    const ratio = contrast(candidate, surface);
    if (ratio > bestRatio) {
      best = candidate;
      bestRatio = ratio;
    }
    if (ratio >= minRatio) return toHex(candidate);
    if (l === 0 || l === 1) break;
  }
  // Saturated hues cannot always reach 4.5 against a mid surface; return the
  // most legible version rather than the original.
  return toHex(best);
}

// ---------- resolving and applying ----------

/**
 * The full palette for an org.
 *
 * The accent keeps living in `branding.primaryColor`, which is what the email
 * template reads. Splitting it out here means one field, one home, and no
 * chance of the app and the email disagreeing about the colour.
 */
export function resolveTheme(branding: OrgBranding | undefined | null): ResolvedTheme {
  const stored = branding?.theme ?? {};
  const resolved = { ...DEFAULT_THEME };
  for (const spec of THEME_TOKENS) {
    const value = stored[spec.key];
    if (value && parseHex(value)) resolved[spec.key] = value;
  }
  if (branding?.primaryColor && parseHex(branding.primaryColor)) {
    resolved.accent = branding.primaryColor;
  }
  return resolved;
}

/** Splits a resolved palette back into the two fields it is stored in. */
export function toBrandingFields(theme: ResolvedTheme): {
  primaryColor: string;
  theme: OrgTheme;
} {
  const stored: OrgTheme = {};
  for (const spec of THEME_TOKENS) {
    if (spec.key === 'accent') continue;
    stored[spec.key] = theme[spec.key];
  }
  return { primaryColor: theme.accent, theme: stored };
}

/**
 * The contrast floors every palette is held to, whatever a tenant picks.
 *
 * Surfaces are left exactly as chosen — they carry the brand. What is forced is
 * anything someone has to *read* against them, because a tenant with nine
 * colour pickers and good intentions will still produce grey-on-grey, and the
 * result is not a branding decision, it is text nobody can read.
 */
const CONTRAST_FLOOR: Partial<Record<ThemeToken, number>> = {
  // Body text and the fill of every solid control.
  ink: 4.5,
  // Labels, metadata, the line under a task title. Real text, normal size.
  textMuted: 4.5,
  // Placeholders and disabled controls: the large-text and non-text floor.
  textFaint: 3,
};

/**
 * A palette with the readable colours nudged until they are readable.
 *
 * Hue and saturation are kept, so the result still looks like the colour that
 * was chosen. Returns the palette unchanged when it already passes, which is
 * what lets the settings screen tell someone their colour was adjusted.
 */
export function enforceContrast(theme: ResolvedTheme): ResolvedTheme {
  const paper = parseHex(theme.paper);
  if (!paper) return theme;

  const out = { ...theme };
  for (const [token, floor] of Object.entries(CONTRAST_FLOOR) as [ThemeToken, number][]) {
    out[token] = adaptToSurface(theme[token], paper, floor);
  }
  return out;
}

/** Which tokens `enforceContrast` had to change, for the settings screen. */
export function contrastAdjustments(theme: ResolvedTheme): ThemeToken[] {
  const fixed = enforceContrast(theme);
  return (Object.keys(CONTRAST_FLOOR) as ThemeToken[]).filter(
    (token) => fixed[token].toLowerCase() !== theme[token].toLowerCase(),
  );
}
